import http from '../api/http';
import {
  API_IMPORTS_ENTRADAS,
  API_IMPORTS_HISTORICO,
  API_IMPORTS_ORDENS_PRODUCAO,
  API_IMPORTS_PRODUTOS,
  API_IMPORTS_SAIDAS,
} from '../api/apiConfig';
import {
  ImportError,
  ImportHistorico,
  ImportResult,
  ImportType,
} from '../types/imports';
import { PaginatedResponse } from '../types/common';

export interface ImportService {
  importFile: (file: File, tipo: ImportType) => Promise<ImportResult>;
  getHistorico: (params?: { page?: number; page_size?: number }) => Promise<PaginatedResponse<ImportHistorico>>;
}

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null;

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
};

const toStringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const filtered = value
    .map((item) => (typeof item === 'string' ? item : undefined))
    .filter((item): item is string => Boolean(item));
  return filtered.length > 0 ? filtered : undefined;
};

const resolveMessage = (data: UnknownRecord, fallbackMessage?: string): string | undefined => {
  const message =
    (typeof data.message === 'string' && data.message) ||
    (typeof data.mensagem === 'string' && data.mensagem) ||
    (typeof data.erro === 'string' && data.erro) ||
    (typeof data.error === 'string' && data.error) ||
    fallbackMessage;
  return message?.trim() ? message : undefined;
};

const normalizeImportErrors = (data: UnknownRecord): ImportError[] | undefined => {
  const structured =
    (Array.isArray(data.linhas_nao_importadas) && data.linhas_nao_importadas) ||
    (isRecord(data.detalhes) &&
      Array.isArray(data.detalhes.erros_detalhados) &&
      data.detalhes.erros_detalhados);

  if (structured && structured.length > 0) {
    const mapped = structured
      .map((item): ImportError | null => {
        if (typeof item === 'string') {
          return { row: 0, message: item };
        }
        if (!isRecord(item)) {
          return null;
        }
        const row = toNumber(item.linha ?? item.row) ?? 0;
        const field =
          (typeof item.field === 'string' && item.field) ||
          (typeof item.tipo === 'string' && item.tipo) ||
          undefined;
        const message =
          (typeof item.mensagem === 'string' && item.mensagem) ||
          (typeof item.message === 'string' && item.message);
        if (!message) {
          return null;
        }
        return { row, field, message };
      })
      .filter((item): item is ImportError => Boolean(item));

    if (mapped.length > 0) {
      return mapped;
    }
  }

  const rawErrors =
    (Array.isArray(data.errors) && data.errors) ||
    (Array.isArray(data.erros) && data.erros) ||
    (isRecord(data.detalhes) && Array.isArray(data.detalhes.erros) && data.detalhes.erros);

  if (!rawErrors || rawErrors.length === 0) {
    return undefined;
  }

  const mapped = rawErrors
    .map((item): ImportError | null => {
      if (typeof item === 'string') {
        return { row: 0, message: item };
      }
      if (!isRecord(item)) {
        return null;
      }
      const row = toNumber(item.row ?? item.linha) ?? 0;
      const field = typeof item.field === 'string' ? item.field : undefined;
      const message = typeof item.message === 'string' ? item.message : undefined;
      if (!message) {
        return null;
      }
      return { row, field, message };
    })
    .filter((item): item is ImportError => Boolean(item));

  return mapped.length > 0 ? mapped : undefined;
};

const normalizeImportResult = (payload: unknown, fallbackMessage?: string): ImportResult => {
  const data = isRecord(payload) ? payload : {};
  const successFlag = data.success ?? data.sucesso;
  const status = typeof data.status === 'string' ? data.status : undefined;
  const success =
    typeof successFlag === 'boolean'
      ? successFlag
      : successFlag === 'true' || (status ? status !== 'erro' : false);

  const errors = normalizeImportErrors(data);
  const warnings =
    toStringArray(data.warnings) ||
    (isRecord(data.detalhes) ? toStringArray(data.detalhes.avisos) : undefined);

  const successCount =
    toNumber(data.success_count) ??
    toNumber(data.linhas_sucesso) ??
    toNumber(data.itens_criados) ??
    toNumber(data.entradas_criadas) ??
    toNumber(data.saidas_criadas) ??
    toNumber(data.criados) ??
    0;
  const errorCount =
    toNumber(data.error_count) ??
    toNumber(data.linhas_erro) ??
    (errors ? errors.length : 0);

  const totalRows =
    toNumber(data.total_rows) ??
    toNumber(data.total_linhas) ??
    (successCount || errorCount ? successCount + errorCount : 0);

  return {
    success,
    total_rows: Math.max(0, totalRows),
    success_count: Math.max(0, successCount),
    error_count: Math.max(0, errorCount),
    errors,
    warnings,
    message: resolveMessage(data, fallbackMessage),
  };
};

const getErrorResponseData = (error: unknown): unknown | undefined => {
  if (!isRecord(error) || !('response' in error)) {
    return undefined;
  }
  const response = (error as { response?: unknown }).response;
  if (!isRecord(response)) {
    return undefined;
  }
  return response.data;
};

const IMPORT_ENDPOINTS: Record<ImportType, string> = {
  produtos: API_IMPORTS_PRODUTOS,
  entradas: API_IMPORTS_ENTRADAS,
  saidas: API_IMPORTS_SAIDAS,
  'ordens-producao': API_IMPORTS_ORDENS_PRODUCAO,
};

const createImportService = (): ImportService => {
  return {
    importFile: async (file: File, tipo: ImportType): Promise<ImportResult> => {
      const formData = new FormData();
      formData.append('arquivo', file);

      console.log(`ÐY"Ï [ImportService] Uploading file for ${tipo}:`, {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      });

      try {
        const endpoint = IMPORT_ENDPOINTS[tipo];
        const response = await http.post(endpoint, formData);

        console.log(`ƒo. [ImportService] Import successful for ${tipo}:`, response.data);
        return normalizeImportResult(response.data);
      } catch (error) {
        console.error(`ƒ?O [ImportService] Import failed for ${tipo}:`, error);
        const responseData = getErrorResponseData(error);
        if (responseData !== undefined) {
          return normalizeImportResult(responseData, 'Erro ao processar o arquivo.');
        }
        throw error;
      }
    },

    getHistorico: async (params) => {
      const response = await http.get<PaginatedResponse<ImportHistorico>>(
        API_IMPORTS_HISTORICO,
        { params }
      );
      return response.data;
    },
  };
};

export const importService = createImportService();

// ServiÇõos especÇðficos por tipo
export const produtosImportService = {
  import: (file: File) => importService.importFile(file, 'produtos'),
};

export const entradasImportService = {
  import: (file: File) => importService.importFile(file, 'entradas'),
};

export const saidasImportService = {
  import: (file: File) => importService.importFile(file, 'saidas'),
};

export const ordensProducaoImportService = {
  import: (file: File) => importService.importFile(file, 'ordens-producao'),
};
